import React, { useState } from "react";

/**
 * rows: Array of { objective, strategy, lead, resources, timeline }
 * Rows with the same `objective` value are grouped under one label.
 * Click any row to expand/collapse its Lead/Partners and Resources detail.
 */
export default function ObjectiveTable({ rows }) {
  const [expanded, setExpanded] = useState({});

  const toggle = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // Build groups preserving order
  const groups = [];
  const seen = new Map();
  rows.forEach((row, i) => {
    const label = row.objective || "";
    if (!seen.has(label)) {
      seen.set(label, groups.length);
      groups.push({ label, rows: [] });
    }
    groups[seen.get(label)].rows.push({ ...row, _key: i });
  });

  const timelineClass = (tl) => {
    const t = (tl || "").toLowerCase().replace(/[–—]/g, "-").replace(/\s+/g, "-");
    if (t === "year-1") return "tl-y1";
    if (t === "year-2") return "tl-y2";
    if (t.startsWith("year-1-") || t.startsWith("years-1-")) return "tl-y1n";
    if (t.startsWith("years-2-")) return "tl-y2n";
    return "tl-multi";
  };

  return (
    <div className="obj-table" role="table" aria-label="Objective strategies table">
      {/* Column header */}
      <div className="obj-table-head" role="row">
        <span className="objth-strategy">Strategy</span>
        <span className="objth-timeline">Timeline</span>
      </div>

      {groups.map((group) => (
        <div key={group.label} className="obj-group" role="rowgroup">
          {group.label && (
            <div className="obj-group-label" role="columnheader">
              {group.label}
            </div>
          )}

          {group.rows.map((row) => {
            const key = row._key;
            const open = !!expanded[key];
            return (
              <div
                key={key}
                className={`obj-row${open ? " obj-row--open" : ""}`}
                onClick={() => toggle(key)}
                role="row"
                aria-expanded={open}
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggle(key)}
              >
                <div className="obj-row-summary">
                  <span className="obj-expand-icon" aria-hidden="true">
                    {open ? "▾" : "▸"}
                  </span>
                  <span className="obj-strategy-text">{row.strategy}</span>
                  <span className={`obj-timeline-badge ${timelineClass(row.timeline)}`}>
                    {row.timeline}
                  </span>
                </div>

                {open && (
                  <div className="obj-row-detail">
                    <div className="obj-detail-item">
                      <span className="obj-detail-label">Lead / Partners</span>
                      <span className="obj-detail-value">{row.lead}</span>
                    </div>
                    <div className="obj-detail-item">
                      <span className="obj-detail-label">Resources</span>
                      <span className="obj-detail-value">{row.resources}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div className="obj-table-hint">Click any row to view lead partners &amp; resources</div>
    </div>
  );
}
